import { Link, createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import SearchBar from "#/components/searchbar";
import { toAppPath } from "#/lib/router-links";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <NavBar />
      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="mb-10 px-5 text-center text-5xl">
          Search Jobs in Anesthesiology
        </h1>

        <section className="mx-8 space-y-5">
          <SearchBar value="" />
        </section>

        <section className="mx-8 space-y-5">
          <p>
            The Roamdoc job board is for medical professionals searching for
            career advancement opportunities in Anesthesiology.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Physician Anesthesiologist Jobs
          </h2>
          <p>
            Explore a comprehensive range of physician anesthesiologist
            positions across leading healthcare facilities, tailored to suit
            your expertise and career aspirations. Discover opportunities that
            offer competitive salaries, professional growth, and the chance to
            work in diverse clinical environments, from bustling city hospitals
            to specialized surgical centers.
          </p>
          <h3>Top Cities for Anesthesiologists</h3>
          <ul>
            <li>
              <h4>
                <Link {...toAppPath("/anesthesiologist-jobs-in-houston-tx")}>
                  Search anesthesiologist jobs near Houston, TX
                </Link>
              </h4>
              <p>
                Nestled in the heart of the Lone Star State, Dallas, Texas is a
                thriving metropolis with state-of-the-art healthcare facilities
                like Baylor University Medical Center, University of Texas
                Southwestern Medical Center, and Methodist Dallas Medical
                Center.
              </p>
            </li>
            <li>
              <h4>
                <Link {...toAppPath("/anesthesiologist-jobs-in-miami-fl")}>
                  Hiring anesthesiologists near Miami, FL
                </Link>
              </h4>
              <p>
                Located in the vibrant Southeast region of Florida, Miami has
                job opportunities in renowned institutions such as Jackson
                Health System, Baptist Health South Florida, and the University
                of Miami Hospital. Renowned for its stunning beaches, diverse
                culture, and thriving nightlife, this bustling metropolis offers
                an enticing lifestyle and substantial career growth for
                Anesthesiologists.
              </p>
            </li>
            <li>
              <h4>
                <Link {...toAppPath("/anesthesiologist-jobs-in-los-angeles-ca")}>
                  Employment for anesthesiologist near Los Angeles, CA
                </Link>
              </h4>
              <p>
                Within world-renowned hospitals such as the UCLA Medical Center,
                Cedars-Sinai Medical Center, Keck Hospital of USC and beyond,
                impactful roles as Anesthesiologists await in this famed Pacific
                Region hub.
              </p>
            </li>
            <li>
              <h4>
                <Link {...toAppPath("/anesthesiologist-jobs-in-new-york-city-ny")}>
                  Open anesthesiologist positions near New York City, NY
                </Link>
              </h4>
              <p>
                In the vibrant Northeast region, New York City is renowned for
                its world-class hospitals such as New York-Presbyterian, Mount
                Sinai, and NYU Langone, making it an enticing locale.
              </p>
            </li>
            <li>
              <h4>
                <Link {...toAppPath("/anesthesiologist-jobs-in-phoenix-az")}>
                  Anesthesiologist vacancies near Phoenix, AZ
                </Link>
              </h4>
              <p>
                In the heart of the Sonoran Desert, Phoenix, Arizona, is a
                bustling metropolis famed for its natural beauty and year-round
                sunshine, offering ample opportunities for Anesthesiologist
                jobs. Explore positions in esteemed hospitals such as Mayo
                Clinic, Banner University Medical Center, and Phoenix Children’s
                Hospital that offer top-tier resources to further your career as
                an Anesthesiologist.
              </p>
            </li>
          </ul>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Anesthesiologist Physician Assistant (APA) Jobs
          </h2>
          <p>
            Dive into a variety of Anesthesiologist Physician Assistant (APA)
            roles, where you can enhance your skills and knowledge in
            anesthesiology support, working alongside experienced
            anesthesiologists in dynamic medical settings. These positions offer
            a path to professional advancement and the opportunity to play a
            crucial role in patient care and surgical procedures in hospitals
            and clinics nationwide.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Nurse Anesthestist (CRNA) Jobs
          </h2>
          <p>
            Explore a diverse selection of Certified Registered Nurse
            Anesthetist (CRNA) positions, offering the chance to specialize in
            anesthesia care and play a vital role in surgical teams across
            various medical settings. Our listings provide opportunities for
            career growth, competitive salaries, and the chance to contribute
            significantly to patient safety and comfort in operating rooms and
            other procedural areas.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Anesthesia Technician Jobs
          </h2>
          <p>
            Discover a range of Anesthesia Technician job opportunities, where
            you can apply your technical expertise in supporting
            anesthesiologists and nurse anesthetists during critical procedures.
            These positions offer hands-on experience in operating room
            environments, a chance to enhance your technical skills, and play a
            key role in the smooth execution of anesthesia care.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Anesthesiology Job Seeker Career Resources
          </h2>
          <p>
            Unlock your potential in anesthesiology with our comprehensive
            career resources, designed to guide you through every step of your
            job search. From resume building tips and interview techniques to
            industry insights and continuing education opportunities, we provide
            the tools you need to advance your career in this dynamic and
            rewarding medical field.
          </p>
          <h3>Salary Guides</h3>
          <p>
            Navigate the financial aspects of your anesthesiology career with
            our detailed{" "}
            <Link {...toAppPath("/anesthesiologist-salary")}>salary guides</Link>, offering
            up-to-date information on compensation trends, regional variances,
            and factors influencing earning potential in the field.
          </p>
          <h3>Resume Examples</h3>
          <p>
            Enhance your resume writing skills with our tailored{" "}
            <Link {...toAppPath("/anesthesiologist-cv-examples")}>
              anesthesiology resume examples
            </Link>
            , offering inspiration and guidance to showcase your professional
            journey and expertise effectively.
          </p>
          <h3>Career Planning Portal</h3>
          <p>
            Access expert coaching and tailored career advice through our Career
            Planning Portal, designed to support your growth and success in the
            anesthesiology field with personalized strategies and professional
            insights.
          </p>
          <h3>Interview Tips</h3>
          <p>
            Master your anesthesiology job interviews with our expert tips,
            covering everything from common questions and effective
            communication strategies to showcasing your clinical expertise and
            understanding of the latest anesthesia practices.
          </p>
          <h3>Create Job Alert</h3>
          <p>
            Set up personalized notifications for job alerts, and be instantly
            informed about the latest anesthesiology opportunities that align
            with your career goals and professional expertise.
          </p>
          <h3>Upload Resume</h3>
          <p>
            Maximize your career opportunities by uploading your resume,
            allowing top healthcare employers to discover your skills and
            expertise in the field of anesthesiology.
          </p>
          <h3>Networking Events</h3>
          <p>
            Attend specialized{" "}
            <Link {...toAppPath("/anesthesiology-events")}>
              networking events tailored for anesthesiology professionals
            </Link>
            , offering unparalleled opportunities to connect with industry
            leaders, peers, and potential employers to expand your professional
            horizons.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Employer Tools
          </h2>
          <p>
            Enhance your recruitment strategy with our comprehensive Roamdoc
            employer tools, enabling you to effortlessly search for candidates
            and connect with top-tier anesthesia professionals. Leverage
            targeted job postings, advanced candidate search features, and more
            to find the ideal match for your healthcare team, streamlining the
            hiring process for anesthesiologists, APAs, and CRNAs.
          </p>
          <h3>Salary Guides</h3>
          <p>
            Access up-to-date salary guides to gain insights into competitive{" "}
            <Link {...toAppPath("/anesthesiologist-salary")}>
              compensation trends in the anesthesiology field
            </Link>
            , helping you to attract and retain top talent with attractive and
            market-aligned salary offerings.
          </p>
          <h3>Resume Database</h3>
          <p>
            Access our extensive Resume Database to effortlessly search and
            discover a diverse pool of highly qualified candidates, making it
            easier to identify and reach out to the ideal professionals for your
            anesthesiology team.
          </p>
          <h3>Hiring Solutions Provider</h3>
          <p>
            As a leading job matching service, Roamdoc offers innovative and
            customizable tools to assist employers in attracting, evaluating,
            and{" "}
            <Link {...toAppPath("/recruit-anesthesia-providers")}>
              retaining the best talent in anesthesiology
            </Link>
            , ensuring a seamless and effective recruitment experience.
          </p>
          <h3>Online Recruitment Platform</h3>
          <p>
            Our online recruitment platform offers a user-friendly, efficient,
            and highly effective way for employers to post jobs, manage
            applications, and connect with a vast pool of qualified anesthesia
            professionals.
          </p>
          <button
            type="button"
            className="rounded-lg bg-sky-500 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-800 focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            Post a Job
          </button>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Find Locum Tenens Assignments
          </h2>
          <p>
            Navigate the diverse opportunities in Locum Tenens, tailored for{" "}
            <Link {...toAppPath("/locum-work-physicians-guide")}>
              physicians seeking varied short-term assignments
            </Link>
            . Gain insights into how this flexible model can enhance your
            career, offering a harmonious blend of professional growth and
            personal freedom.
          </p>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={addJsonLd()}
      />
    </>
  );
}

export const metadata: PageMetadata = {
  title: "Search Jobs in Anesthesiology Near You",
  description:
    "Find Anesthesiology jobs and hospitals hiring in the Texas, Florida, California, New York and all of U.S.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/anesthesiology-job-search`,
  },
};

function addJsonLd() {
  return {
    __html: `{
      "@context": "https://schema.org/",
      "@type": "WebPage",
      "url": "https://www.roamdoc.com/anesthesiology-job-search",
      "logo": "hhttps://www.roamdoc.com/roamdoc-logo.png",
      "inLanguage": "en-US",
      "description": "Job search engine for physicians in anesthesiology."
    }`,
  };
}

export const Route = createFileRoute("/(job-search)/anesthesiology-job-search/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
